import _ from "lodash";
import React, { PureComponent, useState, memo, useRef } from "react";
import { compose } from "lib/apolloClient";
import classes from "./RubricTable.scss";
import TextAreaInput from "UIComponents/TextAreaInput";
import I18nHOC from "UIComponents/I18nHOC";
import { colors, fontStyle } from "Constants";
import { connect } from "react-redux";
import classNames from "classnames";
import {
  createRubricIndicator,
  writeRubricElementLocal,
  deleteRubricIndicator,
  createRubricCriteria,
  deleteRubricCriteria,
} from "Rubric/modules/RubricModule";
import { updateField } from "modules/FieldUpdateThunks";

import { Button, IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  AddOutlined,
  DeleteOutlined,
  TickCircleFilled,
  SinglePointRubricOutlined,
  ConfigureColumnsOutlined,
  AssessmentApplicableOutlined,
  CircleOutlined,
} from "@toddle-edu/ds-icons";
import AssessmentToolAccordionTable from "AppComponents/AssessmentToolAccordionTable";
import AIJustification from "UIComponents/AIJustification";
import RubricTableWithReordering from "../RubricTableWithReordering";
import { RUBRIC } from "Constants/stringConstants";
import { scrollIntoView } from "Utils";
import {
  getAssessmentToolStyles,
  getGroupedJustifications,
  getStudentDetailsWithArchivedStatus,
} from "ClassRoom/modules/utils";

import { saveAssessmentToolUpdateStatusInRedux } from "Assessments/modules/AssessmentModule";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import StudentListPopover from "UIComponents/StudentListPopover";

const textAreaStyles = {
  cell: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    color: colors.textSubtle,
    lineHeight: 1.57,
    width: "100%",
    fontSize: "1.6rem",
    padding: "16px",
    overflow: "hidden",
    minHeight: "100%",
    ...fontStyle.medium,
  },
  rowHeaderCell: {
    color: colors.textDefault,
    padding: "16px 0 16px 16px",
  },
  headerCell: {
    color: "var(--text-primary)",
    fontSize: "1.6rem",
    padding: "8px 16px",
    height: "unset",
    ...fontStyle.medium,
  },
};

const globalStyles = {
  aiTick: {
    color: "var(--interactive-tertiary)",
  },
};

const ColumnCell = memo(props => {
  const {
    setFocusedColumn,
    isEvaluation,
    isSelected,
    isStudentSelected,
    descriptor,
    updateEvaluationCell,
    showInsight,
    updateInputRef,
    setFocusedCell,
    refKey,
    writeRubricElement,
    responseCount,
    insightColor,
    mode,
    criteriaObjId,
    userType,
    cellStyle,
    studentArray,
    shouldTakeFullWidth,
    t,
    textAreaCustomStyles,
    minRows,
    showHoverState = false,
    writeRubricElementLocal,
    saveRubricUpdateActivityStatus,
    isAISelected,
    rowIndex,
    columnIndex,
    evaluationCellRefs,
    testId,
    isBottomRightCell,
    bottomRightCellStyle,
    showRevampedEvaluationUI,
    textAreaContainerStyle,
    showOnlyDefaultCursor,
    isGeneratingAIResponse,
    onAcceptAIEvaluation,
    isAiEvaluationEnabled,
    aiToolEvaluationResponses,
  } = props;

  const [descriptorLabel, setDescriptorLabel] = useState(
    descriptor?.label || ""
  );
  const [showPopover, setShowPopover] = useState(false);

  const containerRef = useRef(null);

  const disabled = _.includes(descriptor?.id, "NEW");

  const handleOnHover = async () => {
    if (_.isEmpty(studentArray)) return;
    setShowPopover(true);
  };

  const handleOnHoverOut = () => {
    setShowPopover(false);
  };

  const cellClass = classNames(
    { [classes.cell]: true },
    { [classes.clickableCell]: isEvaluation && userType == "staff" },
    {
      [classes.clickableStudentCell]:
        isEvaluation && userType == "student" && !showRevampedEvaluationUI,
      [classes.clickableStudentCellV2]:
        isEvaluation && userType == "student" && showRevampedEvaluationUI,
    },
    {
      [classes.aiSelectableCell]: isAiEvaluationEnabled
        ? isAISelected && !isGeneratingAIResponse
        : isAISelected && !isSelected,
    },
    {
      [classes.selectableCell]:
        isSelected &&
        !showRevampedEvaluationUI &&
        (isAiEvaluationEnabled
          ? !isGeneratingAIResponse && !isAISelected
          : true),
      [classes.selectableCellV2]:
        isSelected &&
        showRevampedEvaluationUI &&
        (isAiEvaluationEnabled
          ? !isAISelected && !isGeneratingAIResponse
          : true),
    },
    {
      [classes.selectableStudentCell]:
        !isSelected && isStudentSelected && !showRevampedEvaluationUI,
      [classes.selectedStudentEvaluationOpenCell]:
        !isSelected &&
        isStudentSelected &&
        showRevampedEvaluationUI &&
        isEvaluation,
      [classes.selectedStudentEvaluationClosedCell]:
        !isSelected &&
        isStudentSelected &&
        showRevampedEvaluationUI &&
        !isEvaluation,
    },
    {
      [classes.responsiveCell]: shouldTakeFullWidth,
    },
    {
      [classes.disabled]: disabled,
    },
    {
      [classes.cursorNotAllowed]: !isEvaluation && !showOnlyDefaultCursor,
    },
    {
      [classes.cellHover]: showHoverState,
    }
  );

  const moreComponent = (
    <div
      ref={containerRef}
      onMouseEnter={handleOnHover}
      onMouseLeave={handleOnHoverOut}
      className={classes.insight}
    >
      <div className={classes.insightCount} style={{ color: insightColor }}>
        {responseCount}
      </div>
      <div className={classes.insightStudent}>
        {t("common:student", { count: responseCount })}
      </div>
      {showPopover ? (
        <StudentListPopover
          studentList={getStudentDetailsWithArchivedStatus({
            studentArray,
          })}
          popOverContainerClasses={classes.popoverContainer}
          containerRef={containerRef}
          position={["top"]}
        />
      ) : null}
    </div>
  );

  const updateInputField = value => {
    setDescriptorLabel(value);
    writeRubricElement({
      elementDetails: descriptor,
      id: _.get(descriptor, "id"),
      label: value,
      type: "DESCRIPTOR",
    });
    saveRubricUpdateActivityStatus();
  };

  const updateCellRef = ref => {
    evaluationCellRefs[`${rowIndex}_${columnIndex}`] = ref;
  };

  const handleClickEvaluation = () => {
    const rowId = _.get(descriptor, "indicator.id", "");
    const columnId =
      (userType == "staff" && isSelected) ||
      (userType == "student" && isStudentSelected)
        ? null
        : _.get(descriptor, "criteria.id");

    const visibleAIResponse =
      isAiEvaluationEnabled &&
      userType == "staff" &&
      _.find(
        aiToolEvaluationResponses,
        response => response.rowId === rowId && !response.isDeleted
      );

    if (isEvaluation) {
      const isDeleteAiRatings =
        !_.isEmpty(columnId) && !_.isEmpty(visibleAIResponse);

      updateEvaluationCell({
        rowId,
        columnId,
        isSelected,
        isDeleteAiRatings,
      });

      if (isDeleteAiRatings) {
        onAcceptAIEvaluation({
          rowId,
          columnId,
        });
      }
    }
  };

  const handleKeyDown = e => {
    if (!isEvaluation) {
      return;
    }

    switch (e?.key) {
      case "Enter":
      case " ":
        e?.preventDefault();
        handleClickEvaluation();
        break;
      case "ArrowRight":
        evaluationCellRefs[`${rowIndex}_${columnIndex + 1}`]?.focus();
        break;
      case "ArrowLeft":
        evaluationCellRefs[`${rowIndex}_${columnIndex - 1}`]?.focus();
        break;
      case "ArrowDown":
        evaluationCellRefs[`${rowIndex + 1}_${columnIndex}`]?.focus();
        break;
      case "ArrowUp":
        evaluationCellRefs[`${rowIndex - 1}_${columnIndex}`]?.focus();
        break;
    }
  };

  const styles = {
    ...cellStyle,
    ...(isBottomRightCell ? bottomRightCellStyle : {}),
  };

  const renderTextArea = () => {
    return (
      <TextAreaInput
        value={descriptorLabel}
        textAreaStyles={{
          ...textAreaStyles.cell,
          ...textAreaCustomStyles,
          ...(showRevampedEvaluationUI ? { padding: "0" } : {}),
        }}
        updateInputRef={ref => updateInputRef({ ref, refKey })}
        onBlurInputField={() => {
          setFocusedCell(null);
          writeRubricElementLocal({
            elementDetails: descriptor,
            id: _.get(descriptor, "id"),
            label: descriptorLabel,
            type: "DESCRIPTOR",
          });
        }}
        onFocusInputField={() => setFocusedCell(refKey)}
        emptyText={""}
        disabled={disabled}
        mode={mode}
        placeholder={t("common:definition")}
        updateInputField={updateInputField}
        className={classes.placeholderColor}
        minRows={minRows}
        testId={`${testId}-rubricTable-cell-${descriptor?.id}`}
        containerStyle={textAreaContainerStyle}
      />
    );
  };

  return (
    <div
      ref={updateCellRef}
      tabIndex={isEvaluation ? 0 : null}
      onFocus={() => {
        setFocusedColumn(criteriaObjId);
      }}
      className={cellClass}
      style={styles}
      key={descriptor?.id}
      onClick={handleClickEvaluation}
      onKeyDown={handleKeyDown}
    >
      {isGeneratingAIResponse ? (
        <SkeletonLoader />
      ) : (
        <>
          {(isSelected || isStudentSelected || isAISelected) &&
          !showRevampedEvaluationUI ? (
            <div className={classes.tickSvgContainer}>
              {/* Student */}
              {isStudentSelected ? (
                <div
                  style={{ marginRight: isSelected ? "8px" : "0px" }}
                  className={classes.tickSvgComponent}
                >
                  <TickCircleFilled size={"xx-small"} />
                </div>
              ) : null}

              {/* Teacher or AI */}
              {isSelected && (isAiEvaluationEnabled ? !isAISelected : true) ? (
                <div className={classes.tickSvgComponent}>
                  <TickCircleFilled size={"xx-small"} variant={"link"} />
                </div>
              ) : isAISelected ? (
                <div className={classes.tickSvgComponent}>
                  <TickCircleFilled
                    size={"xx-small"}
                    style={globalStyles.aiTick}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          {!showInsight ? (
            showRevampedEvaluationUI ? (
              <div className={classes.textAreaWithTickIcon}>
                {renderTextArea()}
                {isSelected || isStudentSelected ? (
                  <div className={classes.tickIconContainer}>
                    {isStudentSelected ? (
                      <TickCircleFilled
                        size={isEvaluation ? "xx-small" : "xxx-small"}
                      />
                    ) : null}
                    {isSelected ? (
                      <TickCircleFilled
                        size={isEvaluation ? "xx-small" : "xxx-small"}
                        variant={"link"}
                      />
                    ) : null}
                  </div>
                ) : isEvaluation ? (
                  <CircleOutlined size={"x-small"} variant="subtle" />
                ) : null}
              </div>
            ) : (
              renderTextArea()
            )
          ) : (
            <div style={{ ...textAreaStyles.cell, paddingTop: 12 }}>
              {moreComponent}
              <div className={classes.insightDescriptor}>{descriptorLabel}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
});

ColumnCell.displayName = "ColumnCell";

const IndicatorCell = memo(props => {
  const {
    t,
    firstColumnCellStyle = {},
    firstColumnRowsCellStyle = {},
    textAreaCustomStyles = {},
    indicatorObj,
    mode,
    shouldTakeFullWidth,
    indicators,
    minRows,
    updateInputRef,
    setFocusedCell,
    writeRubricElement,
    deleteRubricIndicator,
    writeRubricElementLocal,
    saveRubricUpdateActivityStatus,
    testId,
    isBottomLeftCell,
    bottomLeftCellStyle,
  } = props;

  const [indicatorLabel, setIndicatorLabel] = useState(
    indicatorObj?.label || ""
  );

  const indicatorObjId = _.get(indicatorObj, "id");
  const refKey = `Indicator_${indicatorObjId}`;
  const disabled = _.includes(indicatorObjId, "NEW");
  const showDeleteIcon = mode !== "view" && _.size(indicators) > 1;

  const updateInputField = value => {
    setIndicatorLabel(value);
    writeRubricElement({
      elementDetails: indicatorObj,
      id: indicatorObjId,
      label: value,
      type: "INDICATOR",
    });
  };

  const firstColumnCellClasses = classNames({
    [classes.firstColumnCell]: true,
    [classes.firstColumnCellEdit]: mode !== "view",
    [classes.disabled]: disabled,
    [classes.responsiveCell]: shouldTakeFullWidth,
  });

  const styles = {
    ...firstColumnCellStyle,
    ...firstColumnRowsCellStyle,
    ...(isBottomLeftCell ? bottomLeftCellStyle : {}),
  };

  return (
    <div className={firstColumnCellClasses} style={styles}>
      <TextAreaInput
        value={indicatorLabel}
        mode={mode}
        updateInputRef={ref => updateInputRef({ ref, refKey })}
        onBlurInputField={() => {
          setFocusedCell(null);
          writeRubricElementLocal({
            elementDetails: indicatorObj,
            id: indicatorObjId,
            label: indicatorLabel,
            type: "INDICATOR",
          });
          saveRubricUpdateActivityStatus();
        }}
        onFocusInputField={() => setFocusedCell(refKey)}
        textAreaStyles={{
          ...textAreaStyles.cell,
          ...textAreaStyles.rowHeaderCell,
          padding: showDeleteIcon ? "16px 0 16px 16px" : "16px",
          ...textAreaCustomStyles,
        }}
        disabled={disabled}
        emptyText={""}
        placeholder={t("common:description")}
        updateInputField={updateInputField}
        className={classes.placeholderColor}
        minRows={minRows}
        testId={`${testId}-rubricTable-cell-${indicatorObjId}`}
      />

      {showDeleteIcon ? (
        <div className={classes.firstColumnCellActionButton}>
          <Tooltip
            tooltip={t("common:delete_with_label", {
              label: t("common:row"),
            })}
            showArrow={true}
          >
            <IconButton
              dsVersion="2.0"
              icon={<DeleteOutlined variant={"subtle"} />}
              testId={`${testId}-rubricTable-cell-${indicatorObjId}-delete`}
              variant="neutral"
              type="plain"
              size={"x-small"}
              onClick={() => deleteRubricIndicator(indicatorObjId)}
              disabled={_.includes(indicatorObjId, "NEW")}
            />
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
});

IndicatorCell.displayName = "IndicatorCell";

const HeaderColumns = memo(props => {
  const {
    t,
    mode,
    rubric,
    cellStyle,
    rowHeaderCellStyle,
    shouldTakeFullWidth,
    textAreaCustomStyles,
    textAreaCustomHeaderStyles = {},
    setFocusedColumn,
    updateInputRef,
    setFocusedCell,
    criteriaObj,
    writeRubricElement,
    deleteRubricCriteria,
    writeRubricElementLocal,
    saveRubricUpdateActivityStatus,
    testId,
    isTopRightCell,
    topRightCellStyle = {},
  } = props;

  const [criteriaLabel, setCriteriaLabel] = useState(criteriaObj?.label || "");

  const criterias = _.get(rubric, "criterias", []);
  const criteriaObjId = _.get(criteriaObj, "id");
  const refKey = `Criteria_${criteriaObjId}`;
  const disabled = _.includes(criteriaObjId, "NEW");

  const updateInputField = value => {
    setCriteriaLabel(value);
    writeRubricElement({
      elementDetails: criteriaObj,
      id: criteriaObjId,
      label: value,
      type: "CRITERIA",
    });
  };

  const rowHeaderCellClass = classNames({
    [classes.rowHeaderCell]: true,
    [classes.rowHeaderCellEdit]: mode !== "view",
    [classes.responsiveCell]: shouldTakeFullWidth,
    [classes.disabled]: disabled,
  });

  const pseudoColumnClass = classNames({
    [classes.pseudoColumn]: true,
    [classes.responsiveCell]: shouldTakeFullWidth,
  });

  const styles = {
    ...rowHeaderCellStyle,
    ...(isTopRightCell ? topRightCellStyle : {}),
  };

  return (
    <div
      className={pseudoColumnClass}
      style={cellStyle}
      onFocus={() => setFocusedColumn(criteriaObjId)}
      key={criteriaObjId}
    >
      <div className={rowHeaderCellClass} style={styles}>
        <TextAreaInput
          updateInputRef={ref => updateInputRef({ ref, refKey })}
          onBlurInputField={() => {
            setFocusedCell(null);
            writeRubricElementLocal({
              elementDetails: criteriaObj,
              id: criteriaObjId,
              label: criteriaLabel,
              type: "CRITERIA",
            });
            saveRubricUpdateActivityStatus();
          }}
          onFocusInputField={() => setFocusedCell(refKey)}
          value={criteriaLabel}
          mode={mode}
          textAreaStyles={{
            ...textAreaStyles.cell,
            ...textAreaStyles.headerCell,
            ...textAreaCustomStyles,
            ...textAreaCustomHeaderStyles,
          }}
          disabled={disabled}
          minRows={1}
          emptyText={""}
          placeholder={t("common:click_to_add_withLabel", {
            label: t("common:criteria"),
          })}
          updateInputField={updateInputField}
          className={classes.placeholderTextOnSubtle}
          testId={`${testId}-rubricTable-cell-${criteriaObjId}`}
        />
        {mode !== "view" && _.size(criterias) > 1 ? (
          <div className={classes.rowHeaderCellActionButton}>
            <Tooltip
              tooltip={t("common:delete_with_label", {
                label: t("common:criteria"),
              })}
              showArrow={true}
            >
              <IconButton
                dsVersion="2.0"
                testId={`${testId}-rubricTable-cell-${criteriaObjId}-delete`}
                icon={<DeleteOutlined variant="subtle" />}
                variant="neutral"
                type="plain"
                size={"small"}
                onClick={() => deleteRubricCriteria(criteriaObjId)}
                disabled={_.includes(criteriaObjId, "NEW")}
              />
            </Tooltip>
          </div>
        ) : null}
      </div>
    </div>
  );
});

HeaderColumns.displayName = "HeaderColumns";

class RubricTable extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      inFocusColumn: null,
      inFocusRow: null,
    };
    if (props.customRef) {
      props.customRef(this);
    }
    this.focusedCell = null;
    this.cellRefs = {};
    this.evaluationCellRefs = {};
    this._isCreatingIndicator = false;
    this._isCreatingCriteria = false;

    this.debouncedWriteRubricElementLocal = _.debounce(
      props.writeRubricElementLocal,
      1000
    );
  }

  componentDidUpdate = prevProps => {
    const { scrollToLastCriteria = false } = this.props;
    const currentCriterias = _.get(this.props, "rubric.criterias", []);
    const prevCriterias = _.get(prevProps, "rubric.criterias", []);

    if (
      scrollToLastCriteria &&
      currentCriterias !== prevCriterias &&
      _.size(currentCriterias) > _.size(prevCriterias)
    ) {
      const criteriaRefId = `Criteria_${_.get(_.last(currentCriterias), "id")}`;

      if (this.cellRefs[criteriaRefId]) {
        scrollIntoView({
          element: this.cellRefs[criteriaRefId],
          block: "nearest",
        });
      }
    }
  };

  blur = () => {
    if (this.focusedCell && this.cellRefs[this.focusedCell]) {
      this.cellRefs[this.focusedCell].blur();
    }
  };

  writeRubricElement = ({ id, type, label, elementDetails }) => {
    const { portalType } = this.props;
    this.debouncedWriteRubricElementLocal({ id, type, label, elementDetails });
    this.props.updateField({
      key: id,
      params: { id, type, label, portalType },
      type: "RUBRIC_ELEMENT",
    });
  };

  saveRubricUpdateActivityStatus = () => {
    const { assignmentId, saveAssessmentToolUpdateStatusInRedux } = this.props;
    saveAssessmentToolUpdateStatusInRedux({ assignmentId, toolType: "RUBRIC" });
  };

  createRubricIndicator = async () => {
    if (this._isCreatingIndicator) return;
    const {
      rubric: { id: rubricId },
      createRubricIndicator,
      setScrollToBottom,
      portalType,
    } = this.props;
    this._isCreatingIndicator = true;
    try {
      await createRubricIndicator({ rubricId, portalType });
      if (setScrollToBottom) {
        setTimeout(setScrollToBottom);
      }
    } finally {
      this._isCreatingIndicator = false;
    }
  };

  createRubricCriteria = async () => {
    if (this._isCreatingCriteria) return;
    const {
      rubric: { id: rubricId },
      createRubricCriteria,
      portalType,
    } = this.props;
    this._isCreatingCriteria = true;
    try {
      await createRubricCriteria({ rubricId, portalType });
    } finally {
      this._isCreatingCriteria = false;
    }
  };

  deleteRubricCriteria = id => {
    const {
      rubric: { id: rubricId },
      deleteRubricCriteria,
      portalType,
    } = this.props;
    deleteRubricCriteria({ id, rubricId, portalType });
  };

  deleteRubricIndicator = id => {
    const {
      rubric: { id: rubricId },
      deleteRubricIndicator,
      portalType,
    } = this.props;

    deleteRubricIndicator({ id, rubricId, portalType });
  };

  getHeaderRow = () => {
    const {
      t,
      mode,
      rubric,
      cellStyle,
      rowHeaderCellStyle,
      shouldTakeFullWidth,
      textAreaCustomStyles,
      textAreaCustomHeaderStyles = {},
      firstColumnCellStyle,
      headerRowStyle = {},
      firstColumnHeaderText = "",
      emptyFirstCellColumnStyle = {},
      topLeftCellStyle = {},
    } = this.props;

    const pseudoFirstColumnCellClass = classNames({
      [classes.pseudoFirstColumn]: true,
      [classes.responsiveCell]: shouldTakeFullWidth,
    });

    const emptyFirstColumnCellClass = classNames({
      [classes.emptyFirstColumnCell]: true,
      [classes.responsiveCell]: shouldTakeFullWidth,
    });

    return (
      <div className={classes.pseudoRow}>
        <div className={classes.headerRow} style={headerRowStyle}>
          <div
            className={pseudoFirstColumnCellClass}
            style={{ ...firstColumnCellStyle, ...topLeftCellStyle }}
          >
            <div
              className={emptyFirstColumnCellClass}
              style={{ ...firstColumnCellStyle, ...emptyFirstCellColumnStyle }}
            >
              {firstColumnHeaderText}
            </div>
          </div>
          {this.getHeaderColumns()}
        </div>
      </div>
    );
  };

  getHeaderColumns = () => {
    const {
      mode,
      rubric,
      cellStyle,
      rowHeaderCellStyle,
      shouldTakeFullWidth,
      t,
      textAreaCustomStyles,
      textAreaCustomHeaderStyles = {},
      writeRubricElementLocal,
      testId,
      topRightCellStyle = {},
    } = this.props;
    const criterias = _.get(rubric, "criterias", []);

    return _.map(criterias, (criteriaObj, idx) => {
      const isTopRightCell = idx === _.size(criterias) - 1;

      return (
        <HeaderColumns
          key={criteriaObj?.id}
          t={t}
          criteriaObj={criteriaObj}
          mode={mode}
          rubric={rubric}
          cellStyle={cellStyle}
          rowHeaderCellStyle={rowHeaderCellStyle}
          shouldTakeFullWidth={shouldTakeFullWidth}
          textAreaCustomStyles={textAreaCustomStyles}
          textAreaCustomHeaderStyles={textAreaCustomHeaderStyles}
          setFocusedColumn={this.setFocusedColumn}
          updateInputRef={this.updateInputRef}
          setFocusedCell={this.setFocusedCell}
          writeRubricElement={this.writeRubricElement}
          deleteRubricCriteria={this.deleteRubricCriteria}
          writeRubricElementLocal={writeRubricElementLocal}
          saveRubricUpdateActivityStatus={this.saveRubricUpdateActivityStatus}
          testId={testId}
          isTopRightCell={isTopRightCell}
          topRightCellStyle={topRightCellStyle}
        />
      );
    });
  };

  getRows = () => {
    const {
      rowStyle,
      writeRubricElementLocal,
      bottomLeftCellStyle = {},
    } = this.props;

    const indicators = _.get(this.props.rubric, "indicators", []);

    return _.map(indicators, (indicatorObj, rowIndex) => {
      const indicatorObjId = _.get(indicatorObj, "id");
      const isLastRow = rowIndex === _.size(indicators) - 1;

      return (
        <div
          className={classes.pseudoRow}
          key={indicatorObjId}
          onFocus={() => {
            this.setState({ inFocusRow: indicatorObjId });
          }}
        >
          <div className={classes.row} style={rowStyle}>
            <IndicatorCell
              {...this.props}
              indicators={indicators}
              indicatorObj={indicatorObj}
              updateInputRef={this.updateInputRef}
              setFocusedCell={this.setFocusedCell}
              writeRubricElement={this.writeRubricElement}
              deleteRubricIndicator={this.deleteRubricIndicator}
              writeRubricElementLocal={writeRubricElementLocal}
              saveRubricUpdateActivityStatus={
                this.saveRubricUpdateActivityStatus
              }
              isBottomLeftCell={isLastRow}
              bottomLeftCellStyle={bottomLeftCellStyle}
            />
            {this.getColumns(indicatorObjId, rowIndex, isLastRow)}
          </div>
        </div>
      );
    });
  };

  setFocusedColumn = columnId => {
    this.setState({ inFocusColumn: columnId });
  };

  updateInputRef = ({ ref, refKey }) => {
    this.cellRefs[refKey] = ref;
  };

  setFocusedCell = refKey => {
    this.focusedCell = refKey;
  };

  getColumns = (indicatorId, rowIndex, isLastRow) => {
    const {
      mode,
      isEvaluation,
      updateEvaluationCell,
      isEvaluatedByStudent,
      toolEvaluationResponses,
      showInsight,
      insights,
      insightColor,
      userType,
      cellStyle,
      shouldTakeFullWidth = false,
      t,
      textAreaCustomStyles,
      minRows,
      showHoverState,
      writeRubricElementLocal,
      testId,
      bottomRightCellStyle = {},
      showRevampedEvaluationUI,
      textAreaContainerStyle = {},
      isSharedWithStudent,
      onAcceptAIEvaluation,
      isAiEvaluationEnabled,
      showOnlyDefaultCursor,
      isGeneratingAIResponse,
    } = this.props;
    // const { criterias, descriptors } = this.props.rubric;
    const criterias = _.get(this.props.rubric, "criterias", []);
    const descriptors = _.get(this.props.rubric, "descriptors", []);

    const groupedToolEvaluationResponse = _.groupBy(
      toolEvaluationResponses,
      toolEvaluationResponse => _.get(toolEvaluationResponse, "creatorType")
    );

    const staffToolEvaluationResponses = _.get(
      groupedToolEvaluationResponse,
      "STAFF",
      []
    );

    const studentToolEvaluationResponses = _.get(
      groupedToolEvaluationResponse,
      "STUDENT",
      []
    );

    const aiToolEvaluationResponses = _.get(
      groupedToolEvaluationResponse,
      "AI",
      []
    );

    const isRowEvaluatedByTeacher = _.some(
      staffToolEvaluationResponses,
      response => _.get(response, "rowId") == indicatorId
    );

    return _.map(criterias, (criteriaObj, columnIndex) => {
      const descriptor = _.find(
        descriptors,
        des =>
          _.get(des, "indicator.id") == indicatorId &&
          _.get(des, "criteria.id") == _.get(criteriaObj, "id")
      );

      const refKey = `Descriptor_${descriptor?.id}`;

      const responseCount = _.get(
        _.find(
          insights,
          insight =>
            _.get(insight, "rowId") == _.get(descriptor, "indicator.id") &&
            _.get(insight, "columnId") == _.get(descriptor, "criteria.id")
        ),
        "responseCount",
        0
      );

      const studentArray = _.get(
        _.find(
          insights,
          insight =>
            _.isEqual(
              _.get(insight, "rowId", ""),
              _.get(descriptor, "indicator.id", "")
            ) &&
            _.isEqual(
              _.get(insight, "columnId"),
              _.get(descriptor, "criteria.id")
            )
        ),
        "studentsList",
        []
      );

      let isAISelected =
        _.findIndex(
          aiToolEvaluationResponses,
          response =>
            _.get(response, "rowId") == _.get(descriptor, "indicator.id") &&
            _.get(response, "columnId") == _.get(descriptor, "criteria.id") &&
            !response.isDeleted
        ) >= 0;

      let isSelected =
        _.findIndex(
          staffToolEvaluationResponses,
          response =>
            _.get(response, "rowId") == _.get(descriptor, "indicator.id") &&
            _.get(response, "columnId") == _.get(descriptor, "criteria.id")
        ) >= 0;

      // TODO: Remove later, Temp code to handle AI evaluation for student
      // - For student we need to present the AI evaluation as if it were the teacher's evaluation
      // - A single row can contain both teacher and AI evaluations (if teacher updated the evaluation after AI)
      //   in that case, we need to show the teacher's evaluation
      if (userType !== "staff") {
        // Hide AI selection if teacher evaluated or content not shared with student
        if (isRowEvaluatedByTeacher || !isSharedWithStudent) {
          isAISelected = false;
        }
        // Show AI evaluation as teacher evaluation for students when appropriate
        else if (!isSelected && isAISelected && isSharedWithStudent) {
          isSelected = true;
          isAISelected = false;
        }
      }

      const isStudentSelected =
        _.findIndex(
          studentToolEvaluationResponses,
          response =>
            _.get(response, "rowId") == _.get(descriptor, "indicator.id") &&
            _.get(response, "columnId") == _.get(descriptor, "criteria.id")
        ) >= 0 && isEvaluatedByStudent;

      const isBottomRightCell =
        columnIndex === _.size(criterias) - 1 && isLastRow;

      return (
        <ColumnCell
          t={t}
          key={criteriaObj?.id}
          criteriaObjId={criteriaObj?.id}
          setFocusedColumn={this.setFocusedColumn}
          isEvaluation={isEvaluation}
          isSelected={isSelected}
          isStudentSelected={isStudentSelected}
          descriptor={descriptor}
          updateInputRef={this.updateInputRef}
          setFocusedCell={this.setFocusedCell}
          writeRubricElement={this.writeRubricElement}
          insightColor={insightColor}
          showInsight={showInsight}
          responseCount={responseCount}
          refKey={refKey}
          updateEvaluationCell={updateEvaluationCell}
          mode={mode}
          userType={userType}
          cellStyle={cellStyle}
          studentArray={studentArray}
          shouldTakeFullWidth={shouldTakeFullWidth}
          textAreaCustomStyles={textAreaCustomStyles}
          minRows={minRows}
          aiToolEvaluationResponses={aiToolEvaluationResponses}
          showHoverState={showHoverState}
          writeRubricElementLocal={writeRubricElementLocal}
          saveRubricUpdateActivityStatus={this.saveRubricUpdateActivityStatus}
          isAISelected={isAISelected}
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          evaluationCellRefs={this.evaluationCellRefs}
          testId={testId}
          isBottomRightCell={isBottomRightCell}
          bottomRightCellStyle={bottomRightCellStyle}
          showRevampedEvaluationUI={showRevampedEvaluationUI}
          textAreaContainerStyle={textAreaContainerStyle}
          showOnlyDefaultCursor={showOnlyDefaultCursor}
          isGeneratingAIResponse={isGeneratingAIResponse}
          onAcceptAIEvaluation={onAcceptAIEvaluation}
          isAiEvaluationEnabled={isAiEvaluationEnabled}
        />
      );
    });
  };

  renderActionButtons = () => {
    const {
      t,
      mode,
      showActionButtons = true,
      rubric,
      onClickToolSave,
      showSaveTemplateButton,
      bottomContainerStyle = {},
      bottomLeftContainerStyle = {},
      rubricIndicatorButtonProps = {},
      rubricCriteriaButtonProps = {},
      rubricTemplateButtonProps = {},
      actionButtonsUIType = "BUTTON",
      testId,
    } = this.props;

    if (mode === "view" || !showActionButtons) return null;

    const criterias = _.get(rubric, "criterias", []);
    const indicators = _.get(rubric, "indicators", []);

    const disableButtons =
      _.includes(_.last(indicators)?.id, "NEW") ||
      _.includes(_.last(criterias)?.id, "NEW");

    const renderButtons = () => {
      switch (actionButtonsUIType) {
        case "BUTTON": {
          return (
            <>
              <div
                className={classes.borderLeftContainer}
                style={bottomLeftContainerStyle}
              >
                <Button
                  dsVersion="2.0"
                  size={"medium"}
                  variant="neutral"
                  type="outlined"
                  icon={<AddOutlined size={"xx-small"} />}
                  onClick={this.createRubricIndicator}
                  disabled={disableButtons}
                  testId={`${testId}-rubricTable-createRubricIndicator`}
                  {...rubricIndicatorButtonProps}
                >
                  {t("common:add_with_label", { label: t("common:criteria") })}
                </Button>
                <Button
                  dsVersion="2.0"
                  size={"medium"}
                  variant="neutral"
                  type="outlined"
                  icon={<AddOutlined size={"xx-small"} />}
                  onClick={this.createRubricCriteria}
                  disabled={disableButtons}
                  testId={`${testId}-rubricTable-createRubricCriteria`}
                  {...rubricCriteriaButtonProps}
                >
                  {t("common:add_with_label", {
                    label: t("common:levels"),
                  })}
                </Button>
              </div>

              {showSaveTemplateButton ? (
                <Button
                  dsVersion="2.0"
                  variant="neutral"
                  type="outlined"
                  size={"medium"}
                  onClick={onClickToolSave}
                  testId={`${testId}-rubricTable-saveAsTemplate`}
                  {...rubricTemplateButtonProps}
                >
                  {t("classRoom:save_as_template")}
                </Button>
              ) : null}
            </>
          );
        }
        case "ICON_BUTTON": {
          return (
            <>
              <div
                className={classes.borderLeftContainer}
                style={bottomLeftContainerStyle}
              >
                <Tooltip
                  placement={"top"}
                  tooltip={t("common:add_with_label", {
                    label: t("common:criteria"),
                  })}
                  showArrow={true}
                >
                  <IconButton
                    dsVersion="2.0"
                    icon={<SinglePointRubricOutlined variant="neutral" />}
                    variant="neutral"
                    type="plain"
                    size={"small"}
                    disabled={disableButtons}
                    onClick={this.createRubricIndicator}
                    testId={`${testId}-rubricTable-createRubricIndicator`}
                  />
                </Tooltip>

                <Tooltip
                  placement={"top"}
                  tooltip={t("common:add_with_label", {
                    label: t("common:levels"),
                  })}
                  showArrow={true}
                >
                  <IconButton
                    dsVersion="2.0"
                    icon={<ConfigureColumnsOutlined variant="subtle" />}
                    variant="neutral"
                    type="plain"
                    size={"small"}
                    disabled={disableButtons}
                    onClick={this.createRubricCriteria}
                    testId={`${testId}-rubricTable-createRubricCriteria`}
                  />
                </Tooltip>
              </div>

              {showSaveTemplateButton ? (
                <Tooltip
                  placement={"top"}
                  tooltip={t("classRoom:save_as_template")}
                  showArrow={true}
                >
                  <IconButton
                    dsVersion="2.0"
                    testId={`${testId}-rubricTable-saveAsTemplate`}
                    icon={<AssessmentApplicableOutlined variant="subtle" />}
                    variant="neutral"
                    type="plain"
                    size={"small"}
                    onClick={onClickToolSave}
                  />
                </Tooltip>
              ) : null}
            </>
          );
        }
      }
    };
    return (
      <div className={classes.bottomContainer} style={bottomContainerStyle}>
        {renderButtons()}
      </div>
    );
  };

  render() {
    const {
      mode,
      rubric,
      conStyle,
      innerConStyle,
      t,
      isSplitView = false,
      toolEvaluationResponses,
      isAiEvaluationEnabled,
      isGeneratingAIResponse,
      userType,
    } = this.props;

    const isStaff = userType === "staff";

    if (isSplitView) {
      return (
        <AssessmentToolAccordionTable
          {...this.props}
          assessmentTool={rubric}
          toolType={RUBRIC}
          isAiEvaluationEnabled={isAiEvaluationEnabled}
        />
      );
    }

    const evaluationJustifications = getGroupedJustifications({
      indicators: rubric?.indicators,
      criterias: rubric?.criterias,
      toolEvaluationResponses,
    });

    const containerClass = classNames(
      { [classes.container]: true },
      { [classes.viewContainer]: mode == "view" }
    );

    return (
      <React.Fragment>
        <div
          onBlur={() => {
            this.setState({ inFocusColumn: null, inFocusRow: null });
          }}
          className={containerClass}
          style={
            mode == "view"
              ? {
                  ...conStyle,
                  marginTop: 0,
                }
              : { ...conStyle }
          }
        >
          <div className={classes.innerContainer} style={innerConStyle}>
            {rubric ? (
              this.getHeaderRow()
            ) : (
              <div className={classes.emptyText} style={{ padding: "16px" }}>
                {t("common:no_label_defined", { label: "rubric" })}
              </div>
            )}
            {rubric ? this.getRows() : null}

            {isStaff &&
            isAiEvaluationEnabled &&
            !_.isEmpty(evaluationJustifications) ? (
              <div className={classes.aiJustificationContainer}>
                <AIJustification
                  justifications={evaluationJustifications}
                  isAIGeneratingResponse={isGeneratingAIResponse}
                />
              </div>
            ) : null}
          </div>
        </div>
        {this.renderActionButtons()}
      </React.Fragment>
    );
  }
}

const RubricTableWrapper = props => {
  const {
    isEnabledAssessmentToolReordering = false,
    uiType = "",
    mode,
  } = props;

  const { rubricStyles = {} } = getAssessmentToolStyles({
    uiType,
  });

  const RubricTableComp =
    isEnabledAssessmentToolReordering &&
    (mode === "edit" || mode === "create") ? (
      <RubricTableWithReordering {...props} />
    ) : (
      <RubricTable {...props} {...rubricStyles} />
    );

  return RubricTableComp;
};
const mapActionCreators = {
  createRubricCriteria,
  createRubricIndicator,
  deleteRubricCriteria,
  deleteRubricIndicator,
  writeRubricElementLocal,
  updateField,
  saveAssessmentToolUpdateStatusInRedux,
};

const mapStateToProps = (state, ownProps) => {
  return {
    userType: state.login.userInfo.user_type,
  };
};

export default compose(
  I18nHOC({ resource: ["unitPlan", "common", "classRoom"] }),
  connect(mapStateToProps, mapActionCreators)
)(RubricTableWrapper);

RubricTableWrapper.defaultProps = {
  isEvaluation: false,
  toolEvaluationResponses: [],
  isEvaluatedByStudent: false,
  showInsight: false,
  insights: [],
  showSaveTemplateButton: true,
  testId: "appComponents",
  showRevampedEvaluationUI: false,
  showOnlyDefaultCursor: false,
};
